import { useEffect, useRef } from 'react'
import { useFrame } from '@react-three/fiber'

// Keyboard-driven "probe rig": a moving group whose world position is published
// to window.__AVATAR_PROBE_POS__ (and __PROBE_SPHERE_POS__) every frame, so the
// LightProbeVolume + ReflectionCaptureVolume sample the baked lighting at the
// rig instead of the (root-locked) avatar. Shared by ProbeReferenceSphere and
// the env-debug MaterialBallProbe.
//
//   Arrow keys           move on the X/Z floor plane
//   PageUp / PageDown    move up / down (Y)
//   Hold Shift           ~3× faster
//
// Returns { groupRef } to attach to the moving <group>. `onMove(p)` (optional)
// fires each frame with the live [x,y,z].
const MOVE_KEYS = new Set(['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'PageUp', 'PageDown'])
const SPEED = 3.0 // metres / second (Shift = 3×)

export function useProbeRig(position = [0, 1.4, 0], onMove) {
  const groupRef = useRef(null)
  const pos = useRef([position[0], position[1], position[2]])
  const pressed = useRef(new Set())
  const shift = useRef(false)
  const lastLog = useRef(0)

  useEffect(() => {
    const typingInField = (e) => {
      const t = e.target
      return t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' ||
        t.tagName === 'SELECT' || t.isContentEditable)
    }
    const onDown = (e) => {
      if (typingInField(e)) return
      if (e.key === 'Shift') { shift.current = true; return }
      if (MOVE_KEYS.has(e.key)) { pressed.current.add(e.key); e.preventDefault() }
    }
    const onUp = (e) => {
      if (e.key === 'Shift') { shift.current = false; return }
      if (MOVE_KEYS.has(e.key)) pressed.current.delete(e.key)
    }
    window.addEventListener('keydown', onDown)
    window.addEventListener('keyup', onUp)
    return () => {
      window.removeEventListener('keydown', onDown)
      window.removeEventListener('keyup', onUp)
      if (typeof window !== 'undefined') {
        delete window.__AVATAR_PROBE_POS__
        delete window.__PROBE_SPHERE_POS__
      }
    }
  }, [])

  useFrame((_, delta) => {
    const keys = pressed.current
    const step = SPEED * delta * (shift.current ? 3 : 1)
    const p = pos.current
    if (keys.has('ArrowLeft')) p[0] -= step
    if (keys.has('ArrowRight')) p[0] += step
    if (keys.has('ArrowUp')) p[2] -= step
    if (keys.has('ArrowDown')) p[2] += step
    if (keys.has('PageUp')) p[1] += step
    if (keys.has('PageDown')) p[1] -= step

    if (groupRef.current) groupRef.current.position.set(p[0], p[1], p[2])
    if (typeof window !== 'undefined') {
      window.__AVATAR_PROBE_POS__ = [p[0], p[1], p[2]]
      window.__PROBE_SPHERE_POS__ = [p[0], p[1], p[2]]
    }
    onMove?.(p)

    if (keys.size) {
      const now = performance.now()
      if (now - lastLog.current > 200) {
        lastLog.current = now
        console.log(`[probe rig] ${p[0].toFixed(2)}, ${p[1].toFixed(2)}, ${p[2].toFixed(2)}`)
      }
    }
  })

  return { groupRef, posRef: pos }
}
